import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram, MessageCircle } from "lucide-react";
import { useCMS } from "../lib/cms";

const STAY_LINKS = [
  { to: "/properties", label: "The Studio" },
  { to: "/long-stay", label: "Long Stays" },
  { to: "/why-choose-us", label: "Hammersmith" },
  { to: "/contact", label: "Concierge" },
];

const VISIT_LINKS = [
  { to: "/about", label: "Our Story" },
  { to: "/long-stay", label: "What's Included" },
  { to: "/properties", label: "Availability" },
];

const FOOTER_DEFAULTS = {
  closing_overline: "A letter, before you leave",
  closing_headline: "We hope to welcome you to Hammersmith soon.",
  closing_body: "Whether you're here for a quiet week, a steady month, or settling in for the season — our door is open, and the kettle's on.",
  cta_book: "Reserve your stay",
  cta_message: "Message the host",
  signoff: "A boutique London residence — by appointment only.",
};

const BRAND_DEFAULTS = {
  logo_url: "/brand/wls-logo.png",
  wordmark: "West London Stays",
  subline: "S T A Y S",
  whatsapp_link: "https://wa.me/447474759947",
  email: "Westlondonstaysltd@gmail.com",
  phone: "+44 07474 759947",
  address: "Hammersmith W6\nLondon, United Kingdom",
  instagram: "",
};

export default function Footer() {
  const F = useCMS("footer", FOOTER_DEFAULTS);
  const B = useCMS("brand", BRAND_DEFAULTS);
  return (
    <footer
      data-testid="site-footer"
      className="border-t border-[var(--ed-rule)] mt-24 lg:mt-32 pt-20 pb-10 bg-[var(--ed-paper)] text-[var(--ed-ink)]"
    >
      <div className="ed-container">
        {/* Editorial masthead block */}
        <div className="max-w-3xl mx-auto text-center mb-20">
          <p className="ed-overline mb-6">{F.closing_overline}</p>
          <h3 className="ed-display text-3xl sm:text-4xl lg:text-5xl mb-6">
            {F.closing_headline}
          </h3>
          <p className="ed-body max-w-xl mx-auto">
            {F.closing_body}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/properties" data-testid="footer-cta-book" className="ed-btn-primary justify-center">
              {F.cta_book}
            </Link>
            <a
              href={B.whatsapp_link || "https://wa.me/447474759947"}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-cta-whatsapp"
              className="ed-btn-ghost justify-center"
            >
              <MessageCircle size={14} /> {F.cta_message}
            </a>
          </div>
        </div>

        <div className="ed-rule mb-14" />

        <div className="grid grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-12 mb-16">
          <div className="col-span-2 lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <img
                src={B.logo_url || "/brand/wls-logo.png"}
                alt={B.wordmark || "West London Stays"}
                className="h-16 w-auto"
              />
              <div className="flex flex-col leading-none">
                <span className="font-serif text-[1.05rem] tracking-[0.32em] uppercase">
                  {B.wordmark || "West London Stays"}
                </span>
                <span className="ed-issue mt-1.5" style={{ fontSize: "0.6rem", letterSpacing: "0.5em" }}>
                  {B.subline || "S T A Y S"}
                </span>
              </div>
            </div>
            <p className="text-[0.95rem] leading-relaxed text-[var(--ed-ink-soft)] max-w-md mb-7">
              A boutique London residence by the Thames. Hosted, curated, and quietly run.
              Premium short, medium, and long stays in West London — written, not just listed.
            </p>
            <div className="flex gap-2.5">
              {[
                { Icon: Instagram,     href: B.instagram || "#",                                          label: "Instagram" },
                { Icon: MessageCircle, href: B.whatsapp_link || "https://wa.me/447474759947",            label: "WhatsApp" },
                { Icon: Mail,          href: `mailto:${B.email || "Westlondonstaysltd@gmail.com"}`,      label: "Email" },
              ].map(({ Icon, href, label }, i) => (
                <a
                  key={i}
                  href={href}
                  aria-label={label}
                  target={href.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  data-testid={`footer-social-${i}`}
                  className="w-10 h-10 rounded-full border border-[var(--ed-rule)] flex items-center justify-center text-[var(--ed-ink-soft)] hover:text-[var(--ed-emerald)] hover:border-[var(--ed-emerald)] transition-all duration-300"
                >
                  <Icon size={15} strokeWidth={1.6} />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2">
            <h4 className="ed-overline mb-5">Stay</h4>
            <ul className="space-y-3 text-[0.92rem]">
              {STAY_LINKS.map((l, i) => (
                <li key={l.to + i}>
                  <Link to={l.to} className="text-[var(--ed-ink-soft)] hover:text-[var(--ed-emerald)] transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="ed-overline mb-5">Visit</h4>
            <ul className="space-y-3 text-[0.92rem]">
              {VISIT_LINKS.map((l, i) => (
                <li key={l.to + i}>
                  <Link to={l.to} className="text-[var(--ed-ink-soft)] hover:text-[var(--ed-emerald)] transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-2 lg:col-span-3">
            <h4 className="ed-overline mb-5">Concierge</h4>
            <ul className="space-y-3.5 text-[0.92rem] text-[var(--ed-ink-soft)]">
              <li className="flex items-start gap-3">
                <Phone size={14} className="text-[var(--ed-emerald)] mt-1 flex-shrink-0" />
                <a href={`tel:${(B.phone || "+447474759947").replace(/[^0-9+]/g, "")}`} data-testid="footer-phone" className="hover:text-[var(--ed-emerald)] transition-colors">
                  {B.phone || "+44 07474 759947"}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={14} className="text-[var(--ed-emerald)] mt-1 flex-shrink-0" />
                <a
                  href={`mailto:${B.email || "Westlondonstaysltd@gmail.com"}`}
                  data-testid="footer-email"
                  className="hover:text-[var(--ed-emerald)] transition-colors break-all"
                >
                  {B.email || "Westlondonstaysltd@gmail.com"}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={14} className="text-[var(--ed-emerald)] mt-1 flex-shrink-0" />
                <span data-testid="footer-address" className="leading-relaxed whitespace-pre-line">
                  {B.address || "Hammersmith W6\nLondon, United Kingdom"}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="ed-rule mb-7" />

        <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-[0.72rem] text-[var(--ed-ink-muted)] tracking-wide">
            © {new Date().getFullYear()} West London Stays Ltd. All rights reserved.
          </p>
          <p className="font-serif italic text-[0.78rem] text-[var(--ed-ink-muted)]">
            {F.signoff}
          </p>
        </div>
      </div>
    </footer>
  );
}
